import React, { useCallback } from 'react';
import { noop } from 'lodash';

import { useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { Stack } from '@lyearn/ui';
import AsyncView from '@/components/AsyncView';
import * as ContentListModal from '@/components/ContentListModal';
import SortAndFilter from '@/components/FilterManager/SortAndFilter';
import useFilterManagerWithState from '@/components/FilterManager/useFilterManagerWithState';
import useDebounceState from '@/hooks/useDebounceState';
import useEntitySet from '@/hooks/useEntitySet';
import LocalisedSttingInfo from '@/modules/corporate/modules/course/views/CourseBuilder/components/SidebarDetails/Settings/components/Dialogs/components/Certificate/localizedSettingInfo';

import EmptyState from '../EmptyState';
import MicroSkillsTable from '../MicroSkillsTable';
import useMicroSkills from './hooks/useMicroSkills';
import ReadonlySkills from './readonlySkills';
import { SelectSkillsDialogProps } from './types';
import { getSelectSkillDialogSortAndFilterItems } from './utils';

function SelectSkillsDialog(props: SelectSkillsDialogProps) {
  const { t } = useTranslation();
  const {
    className,
    description,
    primaryActionText = t('Skills:AddSkills.AddSkill'),
    primaryActionDisabled,
    readonly,
    selectedSkillIds,
    title = t('Skills:AddSkills.SelectSkills'),
    onAttach,
    onClose,
    filteredSkillIds,
  } = props;
  const [searchInput, setSearchInput, searchString] = useDebounceState('');
  const {
    state: { sort, filter },
    methods: { setSort, setFilter },
  } = useFilterManagerWithState({});
  const sortAndFilterItems = getSelectSkillDialogSortAndFilterItems(t);
  const { skills, fetching } = useMicroSkills({
    searchText: searchString,
    filteredSkillIds,
    orgRoleIds: filter.orgRoleIds as string[],
    pause: readonly,
  });
  const { skills: selectedSkills, fetching: fetching_ } = useMicroSkills({
    ids: selectedSkillIds,
    searchText: searchString,
    orgRoleIds: filter.orgRoleIds as string[],
    pause: !readonly || !selectedSkillIds?.length,
  });
  const { selectedItemsSet, toggle } = useEntitySet(selectedSkillIds);

  const attatchSkills = useCallback(() => {
    onAttach(Array.from(selectedItemsSet));
    onClose();
  }, [onClose, onAttach, selectedItemsSet]);

  return (
    <ContentListModal.Main
      action={
        readonly
          ? undefined
          : {
              onSubmit: attatchSkills,
              text: primaryActionText,
              disabled: primaryActionDisabled,
            }
      }
      className={cn('px-24 pt-12', className)}
      footer={readonly ? <LocalisedSttingInfo /> : undefined}
      header={{
        title: title,
        description: description,
      }}
      searchBar={{
        value: searchInput,
        onChange: setSearchInput,
        placeholder: t('Skills:AddSkills.SkillSearchPlaceholder'),
        selectedCount: selectedItemsSet?.size,
      }}
      sortComponent={
        readonly ? undefined : (
          <SortAndFilter
            className="mt-16 px-8"
            filter={filter}
            setFilter={setFilter}
            setSort={setSort}
            sort={sort}
            sortAndFilterItems={sortAndFilterItems}
          />
        )
      }
      onClose={onClose}>
      <Stack className="h-[43rem] overflow-y-auto">
        {readonly && selectedSkills.length ? (
          <ReadonlySkills skills={selectedSkills} />
        ) : (
          <AsyncView
            data={skills}
            fetching={fetching}
            renderData={(skills) => (
              <MicroSkillsTable
                key={JSON.stringify(skills)}
                selectedSkillIds={Array.from(selectedItemsSet)}
                skills={skills}
                toggle={readonly ? noop : toggle}
              />
            )}
            renderEmptyState={() => <EmptyState searchString={searchString} />}
          />
        )}
      </Stack>
    </ContentListModal.Main>
  );
}

export default SelectSkillsDialog;
